import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../../utils/constant";
import Pagination from "../Pagination/Pagination";
import Select from "react-select";
import { toastr } from "react-redux-toastr";

const ActiveVaV2 = () => {
    // State management
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModal, setModal] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [selectedInterval, setSelectedInterval] = useState("");
    const [searchTag, setSearchTag] = useState("");
    const [editScheduleId, setEditScheduleId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [scheduleToDelete, setScheduleToDelete] = useState(null);
    const [tagInput, setTagInput] = useState(""); // New state for tag input

    // Form data
    const [formData, setFormData] = useState({
        tag: [],
        interval: "daily",
        startsFrom: new Date()
    });

    // Tags suggestion options
    const [uniqueTags, setUniqueTags] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);

    // Get organisation ID
    const id = useSelector(state => {
        return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
    });

    const auth = useSelector((state) => state?.auth?.user);
    const dispatch = useDispatch();

    // Load schedules on component mount and when filters change
    useEffect(() => {
        if (id) {
            fetchSchedules();
        }
    }, [id, page, limit, selectedInterval, searchTag]);

    // Load unique tags for suggestions
    useEffect(() => {
        if (id) {
            fetchUniqueTags();
        }
    }, [id]);

    // Update tag options whenever unique tags change
    useEffect(() => {
        const options = uniqueTags.map(tag => ({
            value: tag,
            label: tag
        }));
        setTagOptions(options);
    }, [uniqueTags]);

    // Fetch all scan schedules
    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            const intervalParam = selectedInterval ? `interval=${selectedInterval}` : '';
            const tagParam = searchTag ? `&tag=${searchTag}` : '';

            const response = await axios.get(
                `${API_URL}/scanschedule/tag/${id}/list?${intervalParam}${tagParam}&page=${page}&limit=${limit}`
            );

            if (response.data && response.data.data) {
                setSchedules(response.data.data);
                setTotalItems(response.data.totalCount || response.data.data.length);
            }
            setLoading(false);
        } catch (error) {
            // console.error removed
            setLoading(false);
        }
    };

    // Fetch unique tags for dropdown
    const fetchUniqueTags = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            const response = await axios.get(
                `${API_URL}/scanschedule/tag/${id}/uniquetags`
            );

            if (response.data && response.data.data) {
                setUniqueTags(response?.data?.tags || []);
            }
        } catch (error) {
            // console.error removed
        }
    };

    // Create new schedule
    const createSchedule = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            // Format tags for the API
            const tags = formData.tag.map(tag => tag.value);

            const requestData = {
                organisationId: id,
                tag: tags,
                interval: formData.interval,
                startsFrom: formData.startsFrom.toISOString()
            };

            const response = await axios.post(`${API_URL}/scanschedule/tag`, requestData);

            if (response.data) {
                toastr.success("Success", response.data.userMessage);
                resetForm();
                fetchSchedules();
                setModal("");
            } else {
                toastr.error("Error", response.data.userMessage);
            }
        } catch (error) {
            // console.error removed
            toastr.error("Error", error.response?.data?.userMessage || "Failed to create schedule");
        }
    };

    // Update existing schedule
    const updateSchedule = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            // Format tags for the API (take first one for update API)
            const tag = formData.tag.length > 0 ? formData.tag[0].value : "";

            const requestData = {
                tag,
                interval: formData.interval,
                startsFrom: formData.startsFrom.toISOString()
            };

            const response = await axios.put(
                `${API_URL}/scanschedule/tag/organisation/${id}/${editScheduleId}/update`,
                requestData
            );

            if (response.data) {
                toastr.success("Success", response.data.userMessage);
                // Reset form and refresh
                resetForm();
                fetchSchedules();
                setModal("");
                setEditScheduleId(null);
            } else {
                toastr.error("Error", response.data.userMessage);
            }
        } catch (error) {
            // console.error removed
            toastr.error("Error", error.response?.data?.userMessage || "Failed to update schedule");
        }
    };

    // Delete schedule
    const deleteSchedule = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            const response = await axios.delete(
                `${API_URL}/scanschedule/tag/organisation/${id}/${scheduleToDelete}/delete`
            );

            if (response.data) {
                toastr.success("Success", response.data.userMessage);
                // Refresh after delete
                fetchSchedules();
                setShowDeleteModal(false);
                setScheduleToDelete(null);
            } else {
                toastr.error("Error", response.data.userMessage);
            }
        } catch (error) {
            // console.error removed
            toastr.error("Error", error.response?.data?.userMessage || "Failed to delete schedule");
        }
    };

    // Handle edit button click
    const handleEditInit = (schedule) => {
        setEditScheduleId(schedule._id);

        // Initialize form data with schedule values
        const selectedTags = Array.isArray(schedule.tag) ?
            schedule.tag.map(t => ({ value: t, label: t })) :
            [{ value: schedule.tag, label: schedule.tag }];

        setFormData({
            tag: selectedTags,
            interval: schedule.interval,
            startsFrom: new Date(schedule.startsFrom || schedule.nextScanAt || Date.now())
        });

        setModal("Form Wizard modal");
    };

    // Handle delete button click
    const handleDeleteInit = (scheduleId) => {
        setScheduleToDelete(scheduleId);
        setShowDeleteModal(true);
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            tag: [],
            interval: "daily",
            startsFrom: new Date()
        });
        setEditScheduleId(null);
    };

    // Handle form submission
    const handleSubmit = () => {
        if (editScheduleId) {
            updateSchedule();
        } else {
            createSchedule();
        }
    };

    // Format interval for display
    const formatInterval = (interval) => {
        return interval.charAt(0).toUpperCase() + interval.slice(1);
    };

    return (
        <div className="container-fluid">
            <div className="block-header">
                <div className="row clearfix">
                    <div className="col-md-6 col-sm-12">
                        <h1 className="welcome-text">Active Va V2</h1>
                        <nav aria-label="breadcrumb" className="mt-1">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/" className="welcome-textSmall">Home</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                                    Active Va
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="col-md-6 col-sm-12 text-right hidden-xs d-flex justify-content-end align-items-center">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                resetForm();
                                setModal("Form Wizard modal");
                            }}
                        >
                            <i className="fa fa-plus mr-2"></i>
                            Add New Tags
                        </button>
                    </div>
                </div>
            </div>

            <div className="card card_tableBox">
                <div className="body">
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <div className="searchBoxInput p-0">
                                <input
                                    className="form-control form-control-sm"
                                    type="text"
                                    placeholder="Search by tag"
                                    value={searchTag}
                                    onChange={(e) => {
                                        setSearchTag(e.target.value);
                                        setPage(1);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <select
                                className="form-control form-control-sm"
                                value={selectedInterval}
                                onChange={(e) => {
                                    setSelectedInterval(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All Intervals</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-custom spacing5 mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Tags</th>
                                    <th>Interval</th>
                                    <th>Next Scan</th>
                                    <th>Created At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : schedules.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            No scan schedules found
                                        </td>
                                    </tr>
                                ) : (
                                    schedules.map((schedule, index) => (
                                        <tr key={schedule._id}>
                                            <td>{(page - 1) * limit + index + 1}</td>
                                            <td>
                                                {Array.isArray(schedule.tag)
                                                    ? schedule.tag.map(tag => (
                                                        <span key={tag} className="badge badge-primary mr-1">{tag}</span>
                                                    ))
                                                    : <span className="badge badge-primary">{schedule.tag}</span>
                                                }
                                            </td>
                                            <td>{formatInterval(schedule.interval)}</td>
                                            <td>
                                                {schedule.nextScanAt
                                                    ? moment(schedule.nextScanAt).format("YYYY-MM-DD HH:mm")
                                                    : "Not scheduled"}
                                            </td>
                                            <td>
                                                {schedule.createdAt
                                                    ? moment(schedule.createdAt).format("YYYY-MM-DD")
                                                    : "N/A"}
                                            </td>
                                            <td>
                                                <Link to="#"
                                                    className="mr-1"
                                                    onClick={() => handleEditInit(schedule)}
                                                >
                                                    Edit
                                                </Link>
                                                <Link
                                                    to="#"
                                                    className="text-danger"
                                                    onClick={() => handleDeleteInit(schedule._id)}
                                                >
                                                    Delete
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-content-center">
                        <div className="dataTables_paginate">
                            {totalItems > 0 && (
                                <Pagination
                                    className="pagination-bar"
                                    currentPage={page}
                                    totalCount={totalItems}
                                    pageSize={limit}
                                    onPageChange={(page) => setPage(page)}
                                />
                            )}
                        </div>
                        {totalItems > 10 && (
                            <select
                                value={limit}
                                onChange={(e) => {
                                    setLimit(parseInt(e.target.value));
                                    setPage(1);
                                }}
                                className="form-control limitSelectBox"
                            >
                                <option value={10}>10 per page</option>
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        )}
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            <div className={`modal fade Form-Wizard-modal ${isModal === "Form Wizard modal" ? "d-block show" : ""}`}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="fa fa-calendar-plus mr-2"></i>
                                {editScheduleId ? "Update Tags" : "Create New Tags"}
                            </h5>
                            <button type="button" className="close" onClick={() => setModal("")}>
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="upload-form">
                                <div className="form-section mb-4">
                                    <h6 className="section-title mb-3">Schedule Details</h6>

                                    <div className="form-group">
                                        <label className="form-label">Tags</label>
                                        <div className="input-group mb-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Type a tag and press Enter or Add"
                                                value={tagInput}
                                                onChange={(e) => setTagInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && tagInput.trim()) {
                                                        e.preventDefault();
                                                        const newTag = { value: tagInput.trim(), label: tagInput.trim() };
                                                        setFormData({ ...formData, tag: [...formData.tag, newTag] });
                                                        setTagInput('');
                                                    }
                                                }}
                                            />
                                            <div className="input-group-append">
                                                <button
                                                    type="button"
                                                    style={{ float: "left", minWidth: "auto", marginLeft: '5px', fontSize: '7px', padding: '6px 16px', fontWeight: '300' }}
                                                    className="btn btn-primary"
                                                    onClick={() => {
                                                        if (tagInput.trim()) {
                                                            const newTag = { value: tagInput.trim(), label: tagInput.trim() };
                                                            setFormData({ ...formData, tag: [...formData.tag, newTag] });
                                                            setTagInput('');
                                                        }
                                                    }}
                                                >
                                                    <i
                                                        className="fa fa-plus"
                                                        style={{ fontSize: "22px", color: "#fff" }}
                                                    ></i>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Display selected tags */}
                                        <div className="d-flex flex-wrap mt-2">
                                            {formData.tag.map((tag, index) => (
                                                <span key={index} className="badge badge-primary mr-2 mb-2 d-flex justify-content-center align-content-center" style={{ fontSize: '14px', padding: '6px 10px' }}>
                                                    {tag.label}
                                                    <button
                                                        type="button"
                                                        className="close ml-2 pl-1"
                                                        style={{ fontSize: '18px', fontWeight: 'normal', lineHeight: '0.85' }}
                                                        onClick={() => {
                                                            const newTags = formData.tag.filter((_, i) => i !== index);
                                                            setFormData({ ...formData, tag: newTags });
                                                        }}
                                                    >
                                                        <span>&times;</span>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>

                                        <small className="form-text text-muted">
                                            {editScheduleId
                                                ? "Note: Only the first tag will be used for updates."
                                                : "Press Enter or click Add after typing each tag"}
                                        </small>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">Interval</label>
                                                <select
                                                    className="form-control"
                                                    value={formData.interval}
                                                    onChange={(e) =>
                                                        setFormData({ ...formData, interval: e.target.value })
                                                    }
                                                >
                                                    <option value="daily">Daily</option>
                                                    <option value="weekly">Weekly</option>
                                                    <option value="monthly">Monthly</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label d-block">Starts From</label>
                                                <DatePicker
                                                    selected={formData.startsFrom}
                                                    onChange={(date) =>
                                                        setFormData({ ...formData, startsFrom: date })
                                                    }
                                                    showTimeSelect
                                                    timeFormat="HH:mm"
                                                    timeIntervals={15}
                                                    timeCaption="Time"
                                                    dateFormat="MMMM d, yyyy h:mm aa"
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-round btn-primary cancelBtn mr-1"
                                onClick={() => {
                                    resetForm();
                                    setModal("");
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={formData.tag.length === 0}
                            >
                                {editScheduleId ? "Update Schedule" : "Create Schedule"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <div className={`modal fade delete-modal ${showDeleteModal ? "d-block show" : ""}`}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Confirm Delete
                            </h5>
                            <button
                                type="button"
                                className="close"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setScheduleToDelete(null);
                                }}
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this scan schedule? This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setScheduleToDelete(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={deleteSchedule}
                            >
                                Delete Schedule
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActiveVaV2;
